/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import { ResponsiveTable } from "../mobile/ResponsiveTable";
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Scale, RefreshCw } from "lucide-react";

const VERDICT = {
  lenient: { zh: "偏松", en: "Lenient", cls: "bg-amber-100 text-amber-800" },
  strict: { zh: "偏严", en: "Strict", cls: "bg-sky-100 text-sky-800" },
  aligned: { zh: "一致", en: "Aligned", cls: "bg-emerald-100 text-emerald-800" },
};
const ROLE = { sales_manager: "销售经理", sales_vp: "销售 VP", admin: "管理员", ceo: "CEO" };

/* 评分质量校准（销售 VP / admin / CEO）：各经理星级(×20) 与 AI 话术评分 的偏差 */
export function RatingCalibration({ api, zh }) {
  const navigate = useNavigate();
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/telephony/rating-calibration", { params: { days } }); setData(d); }
    catch { setData(null); }
  }, [api, days]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const maxH = (h) => Math.max(1, ...Object.values(h || {}));
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="rating-calibration" data-days={days}>
      <div className="flex items-center gap-2 px-4 py-3 flex-wrap">
        <Scale className="h-4 w-4 text-sky-600" />
        <h2 className="text-sm font-semibold text-slate-800">{zh ? "评分质量校准" : "Rating calibration"}</h2>
        <span className="text-xs text-slate-500" data-testid="rating-calibration-totals">
          {zh ? `近 ${data.days} 天 ${data.totals.raters} 位评分人 · ${data.totals.ratings} 次评分 · ${data.totals.pairs} 次可与 AI 对比 · 偏差 ≥ ±${data.threshold} 判定偏松 / 偏严` : `${data.totals.ratings} ratings · ${data.totals.pairs} comparable · threshold ±${data.threshold}`}
        </span>
        <div className="flex-wrap md:flex-nowrap ml-auto flex items-center gap-1">
          {[30, 90, 180].map((n) => (
            <button key={n} data-testid={`rating-calibration-days-${n}`} onClick={() => setDays(n)} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-2.5 py-1 text-xs rounded-md font-medium ${days === n ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`)}>{n}{zh ? "天" : "d"}</button>
          ))}
          <button data-testid="rating-calibration-refresh" onClick={load} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 p-1.5 text-slate-400 hover:text-slate-700"><RefreshCw className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      <div className="overflow-x-auto border-t border-slate-100">
        <ResponsiveTable className="w-full text-sm min-w-[980px]" data-testid="rating-calibration-table">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr>{[zh ? "评分人" : "Rater", zh ? "分公司" : "Branch", zh ? "评分次数" : "Ratings", zh ? "平均星级" : "Avg ★", zh ? "星级分布 1→5" : "Stars 1→5", zh ? "对比样本" : "Pairs", zh ? "经理分(×20)" : "Mgr %", zh ? "AI 平均" : "AI avg", zh ? "偏差" : "Bias", zh ? "判定" : "Verdict", zh ? "对比样例（点击试听）" : "Samples"].map((h, i) => <th key={i} className="px-3 py-2 text-left font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.username} data-testid={`rating-calibration-row-${r.username}`} data-verdict={r.verdict || ""} data-bias={r.bias ?? ""} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="px-3 py-2 font-semibold text-slate-800">{r.name}<span className="ml-1 text-[10px] font-normal text-slate-400">{ROLE[r.role] || ""}</span></td>
                <td className="px-3 py-2 text-slate-600">{r.regionLabel}</td>
                <td className="px-3 py-2">{r.ratings}</td>
                <td className="px-3 py-2">{r.avgStars != null ? `${r.avgStars} ★` : "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex items-end gap-0.5 h-6" title={Object.entries(r.hist).map(([k, v]) => `${k}★ ${v}`).join(" · ")}>
                    {[1, 2, 3, 4, 5].map((k) => <div key={k} className="w-3 rounded-sm bg-amber-400" style={{ height: `${Math.max(8, (r.hist[k] / maxH(r.hist)) * 100)}%`, opacity: r.hist[k] ? 1 : 0.15 }} />)}
                  </div>
                </td>
                <td className="px-3 py-2">{r.pairs}</td>
                <td className="px-3 py-2">{r.avgMgrPct ?? "—"}</td>
                <td className="px-3 py-2">{r.avgAi ?? "—"}</td>
                <td className={`px-3 py-2 font-bold ${r.bias == null ? "text-slate-300" : r.bias >= 15 ? "text-amber-700" : r.bias <= -15 ? "text-sky-700" : "text-emerald-700"}`}>{r.bias == null ? "—" : `${r.bias > 0 ? "+" : ""}${r.bias}`}{r.spread != null && <span className="ml-1 text-[10px] font-normal text-slate-400">±{r.spread}</span>}</td>
                <td className="px-3 py-2">{r.verdict ? <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${VERDICT[r.verdict].cls}`}>{zh ? VERDICT[r.verdict].zh : VERDICT[r.verdict].en}</span> : <span className="text-xs text-slate-400">{zh ? "样本不足" : "n/a"}</span>}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {(r.samples || []).slice(0, 2).map((sm) => (
                      <button key={sm.callId} data-testid={`rating-calibration-sample-${sm.callId}`} onClick={() => sm.agentUsername && navigate(`/agents/${sm.agentUsername}?rate=${sm.callId}`)} title={`${sm.agent} → ${sm.customer}`}
                        className={`rounded border px-1.5 py-0.5 text-[11px] hover:bg-slate-50 ${sm.diff >= 15 ? "border-amber-200 text-amber-800" : sm.diff <= -15 ? "border-sky-200 text-sky-800" : "border-slate-200 text-slate-600"}`}>
                        AI {sm.ai} / {sm.stars}★ <span className="text-slate-400">{sm.agent}</span>
                      </button>
                    ))}
                    {!(r.samples || []).length && <span className="text-slate-300 text-xs">—</span>}
                  </div>
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={11} className="text-center text-slate-300 py-6" data-testid="rating-calibration-empty">{zh ? "暂无经理评分" : "No ratings"}</td></tr>}
          </tbody>
        </ResponsiveTable>
      </div>
      <p className="px-4 py-2 text-[11px] text-slate-400">{zh ? "偏差 = 平均（星级 × 20 − 同一通话 AI 话术评分）；≥ +15 偏松、≤ −15 偏严；至少 3 通可对比通话才判定。连续两周同向偏松 / 偏严将于周一 08:10 自动铃铛提醒销售 VP（附样例通话）。" : "Bias = mean(stars×20 − AI score) over the same calls; ≥+15 lenient, ≤−15 strict; needs ≥3 pairs."}</p>
    </div>
  );
}
